import React from 'react';
import {
  FieldType,
  ISchemaDataModelFieldsFragment,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { DataModelerSelectTableColumn } from '../DataModelerFieldDropdown';
import { FieldLabel } from '../../../../components/Form';
import { useFactRequiredFieldsModel } from './factUtils';
import { RequiredFieldRow } from './DataModelerRequiredFieldsUI';
import DataModelerInstallTemporalsSection from './DataModelerInstallTemporalsSection';
import DataModelerSubjectsSection from './DataModelerSubjectsSection';

interface IProps {
  schemaObj: ISchemaDataModelFieldsFragment;
  isEditMode: boolean;
  tableKey: string;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
}

const DataModelerRequiredFactFields = ({
  schemaObj,
  isEditMode,
  tableKey,
  availableColumnsFromServer,
}: IProps) => {
  const {
    canEdit,
    eventColumn,
    handleSetEventColumn,
    subjectRequired,
    subjectsList,
    addNewSubject,
    removeNewSubject,
    suggestedNextSubjectName,
    updateSubjectColumn,
    subjectNameValidatorFunc,
    updateSubjectName,
    eventTemporalsData,
    handleSetTimestamp,
    handleSetDate,
    installTemporalsData,
    installTemporalsCanInit,
    installTemporalsInit,
    installTemporalsRemove,
    installTemporalsSetDate,
  } = useFactRequiredFieldsModel({
    schemaObj,
    isEditMode,
    tableKey,
    availableColumnsFromServer,
  });

  return (
    <>
      <RequiredFieldRow>
        <div>
          <FieldLabel>Event (required)</FieldLabel>
        </div>
        <div>
          <DataModelerSelectTableColumn
            viewKey={schemaObj.fact.key}
            tableKey={tableKey}
            availableColumnsFromServer={availableColumnsFromServer}
            isDisabled={!canEdit}
            addFieldOnSetColumn={false}
            columnName={eventColumn?.column}
            setColumn={handleSetEventColumn}
            fieldTypeFilter={[FieldType.String]}
          />
        </div>
      </RequiredFieldRow>
      <RequiredFieldRow>
        <div>
          <FieldLabel>Timestamp (required)</FieldLabel>
        </div>
        <div>
          <DataModelerSelectTableColumn
            viewKey={schemaObj.fact.key}
            tableKey={tableKey}
            availableColumnsFromServer={availableColumnsFromServer}
            isDisabled={!canEdit}
            addFieldOnSetColumn={false}
            columnName={eventTemporalsData.ts.column ?? undefined}
            setColumn={handleSetTimestamp}
            fieldTypeFilter={[FieldType.Timestamp]}
          />
        </div>
      </RequiredFieldRow>
      <RequiredFieldRow>
        <div>
          <FieldLabel>Date (required)</FieldLabel>
        </div>
        <div>
          <DataModelerSelectTableColumn
            viewKey={schemaObj.fact.key}
            tableKey={tableKey}
            availableColumnsFromServer={availableColumnsFromServer}
            isDisabled={!canEdit}
            addFieldOnSetColumn={false}
            columnName={eventTemporalsData.date.column ?? undefined}
            setColumn={handleSetDate}
            fieldTypeFilter={[FieldType.Date]}
            customFilter={(item) =>
              !installTemporalsData?.date?.column ||
              installTemporalsData.date.column !== item.column
            }
          />
        </div>
        {!eventTemporalsData.date.column &&
          !availableColumnsFromServer.some(
            (tableColumn) => tableColumn.type === FieldType.Date,
          ) && (
            <div>
              Hint: You can create a Date column from the Event Timestamp using
              a Derived Column
            </div>
          )}
      </RequiredFieldRow>
      <DataModelerSubjectsSection
        key={`edit-subjects-${subjectsList.length}`}
        isEditMode={isEditMode}
        schemaObj={schemaObj}
        tableKey={tableKey}
        availableColumnsFromServer={availableColumnsFromServer}
        subjectRequired={subjectRequired}
        subjectsList={subjectsList}
        addNewSubject={addNewSubject}
        removeNewSubject={removeNewSubject}
        updateSubjectColumn={updateSubjectColumn}
        subjectNameValidatorFunc={subjectNameValidatorFunc}
        suggestedNextSubjectName={suggestedNextSubjectName}
        updateSubjectName={updateSubjectName}
      />
      <DataModelerInstallTemporalsSection
        schemaObj={schemaObj}
        tableKey={tableKey}
        availableColumnsFromServer={availableColumnsFromServer}
        temporalsData={installTemporalsData}
        canEdit={canEdit || (isEditMode && !schemaObj.installTemporals)}
        canInit={installTemporalsCanInit}
        canRemove={canEdit || (isEditMode && !schemaObj.installTemporals)}
        initInstallTemporals={installTemporalsInit}
        removeInstallTemporals={installTemporalsRemove}
        setInstallDate={installTemporalsSetDate}
        eventDateColumn={eventTemporalsData.date.column}
      />
    </>
  );
};

export default DataModelerRequiredFactFields;
